import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { HomeFilter, HomeFilterLevel } from '../models/client-configuration';
import { SearchTerm } from '../state/user/searches/models/search';
import { Util } from '../util';
import { Filter } from '../models/filter';
import { FilterValue } from '../models/filter-value';

@Injectable({
  providedIn: 'root',
})
export class ThemaService {
  themaFilter: HomeFilter;
  private themata: Map<string, string>;

  constructor(configService: ConfigService) {
    this.themata = configService.get().themata;
    this.themaFilter = configService.get().themaFilter;
  }

  /**
   * Returns the description for a Thema code.
   * @param code Thema code
   */
  getDescription(code: string): string {
    return (this.themata ? this.themata[code] : code) || code;
  }

  /**
   * Turns a flat Thema topics filter into a three-level hierarchy of Thema categories using predefined groups.
   * @param topics a Thema HomeFilter
   */
  groupTopics(topics: HomeFilter): HomeFilter {
    if (
      topics.name !== Util.THEMA_FILTER ||
      !this.hasParentLabels(topics.firstLevel[0]) ||
      this.hasNextLevel(topics.firstLevel[0]) ||
      !this.themaFilter
    ) {
      // Not our filter, or already grouped, or backend is running a pre-Thema version
      return topics;
    } else {
      const maps: { [parent: string]: HomeFilterLevel[] } = {};
      for (const topic of topics.firstLevel) {
        if (topic.parentLabels) {
          // when false, it means this topic has a Thema code not included in any group -- skip it
          for (const parentLabel of topic.parentLabels) {
            (maps[parentLabel] = maps[parentLabel] || []).push(topic);
          }
        }
      }
      const groupedTopics = this.buildEmptyGroups();
      for (const firstLevel of groupedTopics.firstLevel) {
        firstLevel.nextLevel = maps[firstLevel.label];
      }
      // Remove empty groups
      groupedTopics.firstLevel = groupedTopics.firstLevel.filter((l) => this.hasNextLevel(l));
      return groupedTopics;
    }
  }

  /**
   * Turns a flat Thema search panel filter into a two-level, optionally-filtered hierarchy of Thema categories using predefined groups.
   * @param filter a Thema Filter
   * @param search an optional search string to filter groups
   */
  groupFilter(filter: Filter, search?: string): FilterValue[] {
    if (filter.name !== Util.THEMA_FILTER || (filter.values[0] && filter.values[0].subFilter) || !this.themaFilter) {
      // Not our filter, or already grouped, or backend is running a pre-Thema version
      return filter.values;
    } else {
      const groupAll = !search;
      const categorizedFilter: FilterValue[] = [];
      for (const firstLevel of this.themaFilter.firstLevel) {
        const fv: FilterValue = {
          name: firstLevel.label,
          criteria: firstLevel.criteria[0],
          value: 0,
          subFilter: [],
        };
        for (const fil of filter.values) {
          if (this.matches(fil, fv)) {
            const desc = this.getDescription(fil.name);
            if (groupAll || desc.toLowerCase().includes(search.toLowerCase())) {
              fv.subFilter.push({
                name: desc,
                criteria: fil.name,
                value: fil.value,
              });
            }
          }
        }
        if (fv.subFilter.length) {
          categorizedFilter.push(fv);
        }
      }
      return categorizedFilter;
    }
  }

  /**
   * Match each search term to its Thema description for it to be shown on the right-hand search panel
   * @param terms a list of SearchTerm
   */
  fillTermLabels(terms: SearchTerm[]) {
    const searchTerms = terms.slice();
    for (const term of searchTerms) {
      if (!term.label && this.themaFilter && term.field === Util.THEMA_FILTER) {
        const labels = this.findFilterLabelsForTerm(this.themaFilter.firstLevel, term.term);
        term.label = labels.shift();
        if (labels.length) {
          for (const label of labels) {
            terms.push({ ...term, label: label });
          }
        }
      }
    }
  }

  private matches(filter: FilterValue, group: FilterValue) {
    for (const crit of group.criteria.replace(/[()*]/g, '').split(' ')) {
      if (filter.name.startsWith(crit)) {
        return true;
      }
    }
    return false;
  }

  private hasNextLevel(l: HomeFilterLevel): boolean {
    return l.nextLevel && !!l.nextLevel.length;
  }

  private hasParentLabels(l: HomeFilterLevel): boolean {
    return l.parentLabels && !!l.parentLabels.length;
  }

  private buildEmptyGroups() {
    return {
      ...this.themaFilter,
      firstLevel: this.themaFilter.firstLevel.map((o) => ({
        ...o,
        nextLevel: [],
      })),
    };
  }

  private findFilterLabelsForTerm(filters: HomeFilterLevel[], term: string): string[] {
    const labels = [];
    for (const firstLevel of filters) {
      if (firstLevel.criteria && firstLevel.criteria[0].includes(term)) {
        labels.push(firstLevel.label);
      }
    }
    if (!labels.length) {
      labels.push(this.getDescription(term));
    }
    return labels;
  }
}
